import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole
} from '@floating-ui/react'
import {
  faChevronDown,
  faLocationDotSlash,
  faSearch
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { type ReactElement, useEffect, useRef, useState } from 'react'
import {
  errorMessage,
  inputError,
  inputLabel,
  inputWrapper
} from '~/components/Form/Input/TextInput.css'
import type { InputInFormProps } from '~/components/Form/Input/input'
import { useFloatingTransition } from '~/hooks/useFloatingTransition'
import { themeClass } from '~/styles/theme.css'
import {
  type AddressOption,
  getPlaceDetails,
  useGoogleAddressSearch
} from '~/utils/address'
import {
  addressSearchInput,
  noResultsMessage,
  searchContainer
} from './AddressDropdown.css'
import {
  dropDownValueBox,
  openIcon,
  openIconOpen,
  optionItem,
  optionsPortal,
  optionsWrapper,
  wrapper,
  wrapperInline
} from './Dropdown.css'
import {
  valueBox,
  valueBoxFullWidth,
  valueBoxPlaceholder,
  valueBoxText
} from './Input.css'

export type AddressDropdownProps = InputInFormProps & {
  name?: string
  label?: string
  placeholder?: string
  defaultValue?: string | null
  onChange?: (address: AddressOption) => void
  geocode?: boolean
  error?: null | { message: string }
}

export const AddressDropdown = ({
  name,
  label,
  placeholder = 'Sélectionner une adresse',
  defaultValue,
  onChange,
  register,
  watch,
  setValue,
  error = null,
  geocode = true
}: AddressDropdownProps): ReactElement => {
  const { addresses, isLoading, searchQuery, setSearchQuery } =
    useGoogleAddressSearch()

  const [selectedAddress, setSelectedAddress] = useState<AddressOption | null>(
    addresses.find((address) => {
      if (watch && name) {
        return address.value === watch(name)
      }
      return address.value === defaultValue
    }) ?? null
  )

  const [isLoadingDetails, setIsLoadingDetails] = useState(false)
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const labelRef = useRef<HTMLLabelElement | null>(null)

  const [isOpen, setIsOpen] = useState(false)

  const { refs, floatingStyles, context } = useFloating({
    placement: 'bottom-start',
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset(5),
      flip(),
      shift(),
      size({
        apply({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
            maxHeight: `${Math.max(0, availableHeight)}px`
          })
        }
      })
    ],
    whileElementsMounted: autoUpdate
  })

  const { isMounted, styles: transitionStyles } = useFloatingTransition(context)

  const click = useClick(context)
  const dismiss = useDismiss(context, {
    outsidePress: (event) => {
      if (labelRef.current?.contains(event.target as Node)) {
        return false
      }
      return true
    }
  })
  const role = useRole(context, { role: 'listbox' })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role
  ])

  if (register !== undefined && name !== undefined) {
    register(name)
  }

  useEffect(() => {
    setSelectedAddress(
      addresses.find((address) => address.value === defaultValue) ?? null
    )
    if (setValue && name && defaultValue) {
      setValue(name, defaultValue)
    }
  }, [defaultValue, addresses, setValue, name])

  useEffect(() => {
    if (isMounted && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    }
  }, [isMounted])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value
    setSearchQuery(query)

    if (setSearchQuery) {
      setSearchQuery(query)
    }
  }

  const handleAddressSelect = async (address: AddressOption) => {
    let selectedAddress = address
    if ((!address.lat || !address.lng) && geocode) {
      setIsLoadingDetails(true)
      try {
        const details = await getPlaceDetails(address.value)
        if (details) {
          selectedAddress = details
        }
      } catch (error) {
        console.error('Error fetching address details:', error)
      } finally {
        setIsLoadingDetails(false)
      }
    }

    setSelectedAddress(selectedAddress)
    setIsOpen(false)

    if (onChange) {
      onChange(selectedAddress)
    }

    if (setValue && name) {
      setValue(name, selectedAddress.value)

      if (selectedAddress.lat && selectedAddress.lng) {
        setValue(`${name}Lat`, selectedAddress.lat)
        setValue(`${name}Lng`, selectedAddress.lng)
      }
    }
  }

  const DropdownEl = (
    <>
      <div className={clsx(wrapper, !label ? wrapperInline : null)}>
        <div
          className={clsx(
            dropDownValueBox,
            valueBox,
            selectedAddress === null ? valueBoxPlaceholder : null,
            valueBoxFullWidth,
            error !== null && inputError
          )}
          {...getReferenceProps()}
          ref={refs.setReference}
        >
          <span className={valueBoxText}>
            {selectedAddress === null ? placeholder : selectedAddress.label}
          </span>
          <FontAwesomeIcon
            icon={faChevronDown}
            className={clsx(openIcon, isOpen ? openIconOpen : null)}
          />
        </div>
        <FloatingPortal>
          {isMounted && (
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              className={optionsPortal}
              {...getFloatingProps()}
            >
              <div
                style={{ ...transitionStyles }}
                className={clsx(themeClass, optionsWrapper)}
              >
                <div className={searchContainer}>
                  <FontAwesomeIcon icon={faSearch} />
                  <input
                    ref={searchInputRef}
                    type="text"
                    className={addressSearchInput}
                    placeholder="Rechercher une adresse..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>

                {isLoading || isLoadingDetails ? (
                  <div>Chargement...</div>
                ) : addresses.length > 0 ? (
                  addresses.map((address) => (
                    <div
                      key={address.value}
                      className={optionItem}
                      onClick={() => handleAddressSelect(address)}
                    >
                      {address.label}
                    </div>
                  ))
                ) : (
                  <div className={noResultsMessage}>
                    <FontAwesomeIcon icon={faLocationDotSlash} />
                    {searchQuery
                      ? 'Aucune adresse trouvée'
                      : 'Commencez à taper pour rechercher'}
                  </div>
                )}
              </div>
            </div>
          )}
        </FloatingPortal>
      </div>
      {error !== null && (
        <div className={errorMessage} role="alert">
          {error.message}
        </div>
      )}
    </>
  )

  if (!label) {
    return DropdownEl
  }

  return (
    <div className={inputWrapper}>
      <label
        htmlFor={name}
        className={inputLabel}
        ref={labelRef}
        onClick={(e) => {
          e.stopPropagation()
          setIsOpen(!isOpen)
        }}
      >
        {label}
      </label>
      {DropdownEl}
    </div>
  )
}
